import { FC } from 'react';

import { DatePicker } from '../../../components/date-picker/index';
import { Cursor, Tooltip } from '../../../components/tooltip/index';
import { DividendHooks } from '../../../hooks/index';
import { useFormat } from '../../../hooks/use-format.hook';
import { StringKey } from '../../../lang/index';
import { selectedCompanyStorage } from '../../../storage/company/index';
import { AccessRole } from '../../../types/stakeholder.types';
import { getTranslation } from '../../../utils/get-translation.util';

type DateField = 'approvalDate' | 'payoutDate';

// The cell looks like plain text, so the hover has to say that clicking opens a date picker.
const EDIT_HINT: Record<DateField, StringKey> = {
  approvalDate: 'EDIT_APPROVAL_DATE',
  payoutDate: 'EDIT_PAYOUT_DATE',
};

type Props = {
  approvalDate: null | string;
  field: DateField;
  payoutDate: null | string;
  payoutId: string;
};

export const DividendInlineDateCell: FC<Props> = ({
  approvalDate,
  field,
  payoutDate,
  payoutId,
}) => {
  const date = field === 'approvalDate' ? approvalDate : payoutDate;

  // A payout cannot be paid before it was approved, so each picker stops at the other date
  // rather than leaving the order to be caught on save.
  const disabledDates =
    field === 'payoutDate'
      ? approvalDate
        ? { before: new Date(approvalDate) }
        : undefined
      : payoutDate
        ? { after: new Date(payoutDate) }
        : undefined;

  const { format } = useFormat();
  const { updateDate, isPending } = DividendHooks.useDividendUpdateDate();

  const isAuditor = selectedCompanyStorage(
    ({ company }) => company?.stakeholder?.accessRole === AccessRole.AUDITOR,
  );

  if (!date) {
    return <span className="text-sm font-[450] text-gray-500">—</span>;
  }

  if (isAuditor) {
    return (
      <span className="text-sm font-[450] text-gray-700">
        {format(new Date(date), 'dd/MM/yyyy')}
      </span>
    );
  }

  const handleSelect = (selected: Date) => {
    updateDate({ id: payoutId, [field]: format(selected, 'yyyy-MM-dd') });
  };

  return (
    <DatePicker
      className="w-fit justify-start p-0"
      disabled={isPending}
      disabledDates={disabledDates}
      onSelect={handleSelect}
      value={new Date(date)}
    >
      {/* A div, not a span: Tooltip only auto-shows on truncated text for span-like children, and
          this hint has to appear whether or not the date fits. */}
      <Tooltip content={getTranslation(EDIT_HINT[field])} cursor={Cursor.POINTER}>
        <div className="w-fit rounded px-2 py-1 text-sm font-[450] text-gray-700 transition-colors hover:bg-brand-50 hover:text-brand-700">
          {format(new Date(date), 'dd/MM/yyyy')}
        </div>
      </Tooltip>
    </DatePicker>
  );
};
